import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { ThreeWayMatchSummary } from '@/types/domain/pipeline';
import { formatCurrency } from '@/lib/utils';
import { CheckCircle2, AlertTriangle, FileCheck, DollarSign, Package } from 'lucide-react';
import { SourceBadge } from '@/components/data-display/SourceBadge';
import { cn } from '@/lib/utils';

interface PipelineThreeWayMatchCardProps {
  threeWayMatch?: ThreeWayMatchSummary;
  currency: string;
}

export const PipelineThreeWayMatchCard: React.FC<PipelineThreeWayMatchCardProps> = ({
  threeWayMatch,
  currency,
}) => {
  if (!threeWayMatch) {
    return (
      <Card className="border-border rounded-2xl shadow-2xs">
        <CardContent className="p-4 text-xs text-foreground-secondary italic font-heading">
          3-Way Matching pending (Invoice or GRN not yet received).
        </CardContent>
      </Card>
    );
  }

  const isMatched = threeWayMatch.matchStatus === 'MATCHED';

  return (
    <Card className="border-border rounded-2xl shadow-2xs font-heading">
      <CardHeader className="pb-3 border-b border-border/60">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-sm font-bold text-foreground flex items-center gap-2">
            <FileCheck className="h-4 w-4 text-primary" />
            Three-Way Financial Matching Visibility
          </CardTitle>
          <SourceBadge source="SAP" showIcon={false} className="text-[9px]" />
        </div>
      </CardHeader>

      <CardContent className="pt-4 space-y-4 text-xs">
        {/* Match Status Banner */}
        <div
          className={cn(
            'flex items-center justify-between p-3 rounded-xl border',
            isMatched
              ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-300'
              : 'bg-rose-500/10 border-rose-500/20 text-rose-700 dark:text-rose-300'
          )}
        >
          <div className="flex items-center gap-2 font-bold">
            {isMatched ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-rose-600" />}
            <span>Matching Status: {threeWayMatch.matchStatus.replace('_', ' ')}</span>
          </div>

          <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded bg-background">
            {isMatched ? '100% Validated' : 'Exception Callout'}
          </span>
        </div>

        {/* 3 Value Cards: PO vs GRN vs Invoice */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl border border-border p-3 bg-card space-y-1">
            <div className="flex items-center gap-1 text-[10px] font-bold text-foreground-muted uppercase">
              <DollarSign className="h-3 w-3 text-primary" /> PO Value
            </div>
            <span className="font-heading text-sm font-extrabold text-foreground block">
              {formatCurrency(threeWayMatch.poAmount, currency)}
            </span>
          </div>

          <div className="rounded-xl border border-border p-3 bg-card space-y-1">
            <div className="flex items-center gap-1 text-[10px] font-bold text-foreground-muted uppercase">
              <Package className="h-3 w-3 text-emerald-600" /> GRN Value
            </div>
            <span className="font-heading text-sm font-extrabold text-foreground block">
              {formatCurrency(threeWayMatch.grnAmount, currency)}
            </span>
          </div>

          <div className="rounded-xl border border-border p-3 bg-card space-y-1">
            <div className="flex items-center gap-1 text-[10px] font-bold text-foreground-muted uppercase">
              <FileCheck className="h-3 w-3 text-purple-600" /> Invoice Amount
            </div>
            <span className="font-heading text-sm font-extrabold text-foreground block">
              {formatCurrency(threeWayMatch.invoiceAmount, currency)}
            </span>
          </div>
        </div>

        {/* Variances */}
        {!isMatched && (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3 space-y-1 text-rose-700 dark:text-rose-300">
            <p className="font-bold">Variance Callout Detected:</p>
            <p className="text-[11px] text-foreground-secondary">
              Price Variance: <strong>{formatCurrency(threeWayMatch.priceVariance, currency)}</strong> • Reason: {threeWayMatch.exceptionReason || 'Discrepancy between PO unit cost and vendor invoice.'}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
